import React, {useEffect, useState} from "react";
import styles from "./SearchForPOI.module.css";
import {
  ContextualMenu,
  IColumn, Icon, IconButton,
  IDropdownOption,
  IStackStyles,
  Spinner,
  Stack,
  StackItem, TooltipOverflowMode,
  MessageBarType
} from "@fluentui/react";
import strings from "../../loc/strings";
import { searchCriteriaForm, SearchError } from "../../services/SearchServices/SearchService";
import { displayMessageBar } from "../LayoutWithAuth/LayoutWithAuth";
import { SearchCriteria } from "../../model/SearchCriteria";
import { useBoolean } from "@fluentui/react-hooks";
import { SearchResultItem } from "../../model/SearchResultItem";
import Pagination from "../Pagination/Pagination";
import { useAuth } from "react-oidc-context";
import { FnPUser } from "../../model/FnPUser";
import {LS_USER_INFO, ROLE_CA_END_USER, ROLE_ESA_END_USER} from "../../constants/EiopaFnPConstants";
import { _legalColumns, _naturalColumns } from "./SearchForPOIUtils";
import PersonOfInterestDetailsList from "../PersonOfInterestDetailsList/PersonOfInterestDetailsList";
import Assessment from "../Assessment/Assessment";
import SearchForm from "../SearchForm/SearchForm";
import {IContextualMenuProps} from "@fluentui/react/lib/ContextualMenu";
import {TooltipHost} from "@fluentui/react/lib/Tooltip";
import EiopaFnPIcons from "../EiopaFnPIcons/EiopaFnPIcons";
import {TypeOption} from "../../model/TypeOption";
import {fetchAssessmentStatus} from "../../services/AssessmentServices/AssessmentService";
import {DateProps} from "../Utils";

interface SearchForPoiProps {}

const stackStylesPagination: Partial<IStackStyles> = {
  root: {
    backgroundColor: "white",
  },
};

const stackStylesNoResults: Partial<IStackStyles> = {
  root: {
    paddingTop: "16px",
  },
};

const detailsListContainer: Partial<IStackStyles> = {
  root: {
    maxHeight: "610px",
    overflowY: "auto"
  }
}

const typeOptions: TypeOption[] = [
  { key: "1", text: "Natural" },
  { key: "2", text: "Legal" },
];

interface SearchForPoiState {
  searchResult: SearchResultItem;
  totalItems: number;
  totalPages: number;
  modalAction: string;
  isExactOnly: boolean;
  isAkaEnabled: boolean;
  loading: boolean;
  isDetailsListVisible: boolean;
  isResultsFetched: boolean;
  selectedKey: string;
  clickedPersonId: number;
  EncryptedId: string;
  name: string;
  searchCriteria: SearchCriteria;
  initialSearchCrit: SearchCriteria;
  columnsState: IColumn[];
  currentPage: number;
  itemsPerPage: number;
  isActionsCalloutVisible: boolean;
  errorMessage: string | null;
}

function SearchForPoi(props: SearchForPoiProps) {
  const auth = useAuth();
  let userInfo: FnPUser = { username: "", ncaCode: "", roles: [] };
  const userInfoLS: string = localStorage.getItem(LS_USER_INFO) as string;
  if (userInfoLS !== null) {
    userInfo = JSON.parse(userInfoLS);
  }
  const isCAEndUser: boolean = userInfo.roles.includes(ROLE_CA_END_USER);
  const isESAEndUser: boolean = userInfo.roles.includes(ROLE_ESA_END_USER);
  const initialSelectedKey = typeOptions[0].key;
  const [isDateValid, setIsDateValid] = useState(true);
  const [hasErrorMessage, setHasErrorMessage] = useState(false);
  const [dateString, setDateString] = useState<string | undefined>(undefined);
  const [dateOfBirth, setDateOfBirth] = useState<Date | undefined>(undefined);
  const [resetFlag, setResetFlag] = useState(false);
  const handleReset = () => {
    setResetFlag(!resetFlag);
  };
  const [state, setState] = React.useState<SearchForPoiState>({
    searchResult: {
      results: [
        {
          PersonId: 0,
          EncryptedId: "",
          FirstName: "",
          LastName: "",
          LegalName: "",
          CountryInc: "",
          LeiRegistration: "",
          CityOfBirth: "",
          CountryOfBirth: "",
          PersonType: "",
          AlsoKnownAs: "",
          DateOfBirth: "",
          SocialSecurityNumber: "",
          ID: "",
          CAInternalAssessmentNo: "",
          personOfInterestId: 0,
          IsExactMatch: false,
          searchScore: 0,
          hasAssesmentOnlyForNca: false
        },
      ],
      totalItems: 0,
      totalPages: 0,
    },
    isActionsCalloutVisible: false,
    modalAction: "",
    isExactOnly: false,
    isAkaEnabled: true,
    loading: false,
    isDetailsListVisible: false,
    isResultsFetched: false,
    selectedKey: initialSelectedKey,
    clickedPersonId: 0,
    EncryptedId: "",
    name: "",
    currentPage: 1,
    itemsPerPage: 10,
    searchCriteria: {
      firstName: "",
      lastName: "",
      legalName: "",
      countryInc: "",
      leiRegistration: "",
      cityOfBirth: "",
      countryOfBirth: "",
      personType: "Natural",
      alsoKnownAs: "",
      dateOfBirth: "",
      socialSecurityNumber: "",
      idNumber: "",
    },
    initialSearchCrit: {
      firstName: "",
      lastName: "",
      legalName: "",
      countryInc: "",
      leiRegistration: "",
      cityOfBirth: "",
      countryOfBirth: "",
      personType: "",
      alsoKnownAs: "",
      dateOfBirth: "",
      socialSecurityNumber: "",
      idNumber: "",
    },
    errorMessage: null,
  } as SearchForPoiState);

  const [isModalOpen, { setTrue: showModal, setFalse: hideModal }] =
      useBoolean(false);

  const _columns =
      state.selectedKey === typeOptions[0].key ? _naturalColumns : _legalColumns;

  const _handleSearchItem = (e: any) => {
    const { name, value } = e.target;
    setState((prevState) => {
      const newState = {
        ...prevState,
        searchCriteria: {
          ...prevState.searchCriteria,
          [name]: value,
        },
      };
      return newState;
    });
  };

  const isGroupNaturalRequired = () => {
    return (
        (state.searchCriteria.idNumber === "" &&
            state.searchCriteria.socialSecurityNumber === "") &&
        (state.searchCriteria.firstName !== "" ||
            state.searchCriteria.lastName !== "" ||
            state.searchCriteria.countryOfBirth !== "" ||
            state.searchCriteria.cityOfBirth !== "" ||
            state.searchCriteria.dateOfBirth !== "")
    );
  };

  const isGroupLegalRequired = () => {
    return (
        state.searchCriteria.legalName !== ""
    );
  };

  const _handleDropDownChange = (event: any, item: any) => {
    setState((prevState) => {
      if (item.key !== prevState.selectedKey) {
        setDateOfBirth(undefined)
        return {
          ...prevState,
          selectedKey: item.key,
          searchCriteria: {
            ...prevState.initialSearchCrit,
            personType: item.text,
          },
          isDetailsListVisible: false,
          currentPage: 1,
          modalAction: item.text,
        };
      }
      return prevState;
    });
  };

  const _onReset = () => {
    handleReset();
    setState({
      ...state,
      searchCriteria: state.initialSearchCrit,
      isResultsFetched: false,
      isExactOnly: false,
      isAkaEnabled: true,
      currentPage: 1,
      errorMessage: null,
    });
    setDateOfBirth(undefined)
    setIsDateValid(true)
    // Clear any displayed error message
    displayMessageBar(MessageBarType.info, "", false);
  };

  const _handleDateChange = (date: any, name: any) => {
    setDateOfBirth(date)
    setState((prevState) => {
      const updatedState = {
        ...prevState,
        searchCriteria: {
          ...prevState.searchCriteria,
          [name]: formatDateToYYYYMMDD(date),
        },
      };
      return updatedState;
    });
  };

  const isDisabled = () => {
    if (state.selectedKey === typeOptions[0].key) {
      if (
          (state.searchCriteria.firstName.trim() !== "" &&
              state.searchCriteria.lastName.trim() !== "" &&
              state.searchCriteria.dateOfBirth && isDateValid && dateOfBirth) ||
          (state.searchCriteria.idNumber.trim() !== "" ||
          state.searchCriteria.socialSecurityNumber.trim() !== "") && !hasErrorMessage
      ) {
        return false;
      }
    } else if (state.selectedKey === typeOptions[1].key) {
      if (
          state.searchCriteria.legalName.trim() !== ""
      ) {
        return false;
      }
    }

    return true;
  };

  function formatDateToYYYYMMDD(date: any) {
    if(date === undefined || date === null) {
      return '';
    }
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");

    const formattedDate = `${year}-${month}-${day}`;
    return formattedDate;
  }

  function formatDateTime(inputDateString: any) {
    if (inputDateString === null || inputDateString === undefined || inputDateString === "") {
      return '';
    }

    const inputDate = new Date(inputDateString);

    const day = String(inputDate.getDate()).padStart(2, '0');
    const month = String(inputDate.getMonth() + 1).padStart(2, '0'); // Months are zero-based
    const year = inputDate.getFullYear();

    const formattedDate = `${day}/${month}/${year}`;

    return formattedDate;
  }

  const _handleSearch = () => {
    const searchCritFormFields: SearchCriteria = {
      firstName: state.searchCriteria.firstName,
      lastName: state.searchCriteria.lastName,
      legalName: state.searchCriteria.legalName,
      countryInc: state.searchCriteria.countryInc,
      leiRegistration: state.searchCriteria.leiRegistration,
      cityOfBirth: state.searchCriteria.cityOfBirth,
      countryOfBirth: state.searchCriteria.countryOfBirth,
      personType: state.searchCriteria.personType,
      alsoKnownAs: state.searchCriteria.alsoKnownAs,
      dateOfBirth: state.searchCriteria.dateOfBirth as unknown as Date,
      socialSecurityNumber: state.searchCriteria.socialSecurityNumber,
      idNumber: state.searchCriteria.idNumber,
    };

    return searchCritFormFields;
  };

  const _handleSearchClick = async () => {
    try {
      // Set loading to true and maintain the previous state
      setState((prevState) => ({ ...prevState, loading: true, currentPage:1, errorMessage: null }));
      // Clear any existing error message
      displayMessageBar(MessageBarType.info, "", false);

      const data = await searchCriteriaForm(
          _handleSearch(),
          1,
          state.itemsPerPage,
          state.isExactOnly,
          state.isAkaEnabled,
          auth.user?.id_token
      );

      // Check if the response is an error
      if ('errors' in data) {
        // Handle validation errors
        const errorMessages = data.errors?.join(', ') || 'An error occurred during search';
        setState((prevState) => ({ 
          ...prevState, 
          errorMessage: errorMessages,
          isResultsFetched: false,
          isDetailsListVisible: false
        }));
        // Display error message in parent component
        displayMessageBar(MessageBarType.error, errorMessages, false);
      } else {
        // Update the state based on the previous state
        setState((prevState) => ({ 
          ...prevState, 
          searchResult: data as SearchResultItem,
          errorMessage: null,
          isResultsFetched: true,
          isDetailsListVisible: true
        }));
      }
    } catch (error) {
      // Handle unexpected errors
      const errorMessage = 'An unexpected error occurred during search';
      setState((prevState) => ({ 
        ...prevState, 
        errorMessage: errorMessage,
        isResultsFetched: false,
        isDetailsListVisible: false
      }));
      // Display error message in parent component
      displayMessageBar(MessageBarType.error, errorMessage, false);
    } finally {
      // Set loading to false and maintain the previous state
      setState((prevState) => ({ ...prevState, loading: false }));
    }
  };

  const _onSearchClick = () => {
    _handleSearchClick();
    // The isResultsFetched and isDetailsListVisible flags will be set in _handleSearchClick
    // based on whether there's an error or not
    setState((prevState) => ({
      ...prevState,
      currentPage: 1,
    }));
  };

  function _isExactMatch() {
    setState((prevState) => ({
      ...prevState,
      isExactOnly: !state.isExactOnly
    }))
  }

  function _isAkaEnabled() {
    setState((prevState) => ({
      ...prevState,
      isAkaEnabled: !state.isAkaEnabled
    }))
  }

  const _items = () => {
    return state.selectedKey === typeOptions[0].key
        ? state.searchResult.results.map((item) => ({
          FirstName: item.FirstName,
          LastName: item.LastName,
          AlsoKnownAs: item.AlsoKnownAs,
          DateOfBirth: formatDateTime(item.DateOfBirth),
          ID: item.ID,
          PersonType: !item.PersonType ? typeOptions[0].text : item.PersonType,
          SocialSecurityNumber: item.SocialSecurityNumber,
          RelevanceScore: item.searchScore,
          IsExactMatch: item.IsExactMatch,
          placeOfBirth: (item.CityOfBirth && item.CountryOfBirth)? `${item.CityOfBirth}, ${item.CountryOfBirth}` : '',
          personId: item.PersonId,
          hasAssesmentOnlyForNca: item.hasAssesmentOnlyForNca,
          EncryptedId: item.EncryptedId,
        }))
        : state.selectedKey === typeOptions[1].key
            ? state.searchResult.results.map((item) => ({
              LegalName: item.LegalName,
              AlsoKnownAs: item.AlsoKnownAs,
              LeiRegistration: item.LeiRegistration,
              CountryInc: item.CountryInc,
              RelevanceScore: item.searchScore,
              PersonType: !item.PersonType ? typeOptions[1].text : item.PersonType,
              IsExactMatch: item.IsExactMatch,
              personId: item.PersonId,
              hasAssesmentOnlyForNca: item.hasAssesmentOnlyForNca,
              EncryptedId: item.EncryptedId,
            }))
            : state.searchResult.results;
  };

  const handleItemClick = (action: string, personId: number, EncryptedId: string, name: string) => {
    showModal();
    setState({
      ...state,
      modalAction: action,
      clickedPersonId: personId,
      EncryptedId: EncryptedId,
      name: name,
    });
  };;

  const _onChangeItemsPerPage = async (
      event: React.FormEvent<HTMLDivElement>,
      item: IDropdownOption
  ) => {

    setState((prevState) => ({
      ...prevState,
      loading: true,
      errorMessage: null,
    }));

    try {
      const data = await searchCriteriaForm(
          _handleSearch(),
          1,
          item.key,
          state.isExactOnly,
          state.isAkaEnabled,
          auth.user?.id_token
      );

      // Check if the response is an error
      if ('errors' in data) {
        // Handle validation errors
        const errorMessages = data.errors?.join(', ') || 'An error occurred during search';
        setState((prevState) => ({ 
          ...prevState, 
          errorMessage: errorMessages,
          loading: false,
          isResultsFetched: false,
          isDetailsListVisible: false
        }));
        // Display error message in parent component
        displayMessageBar(MessageBarType.error, errorMessages, false);
      } else {
        setState((prevState) => ({
          ...prevState,
          searchResult: data as SearchResultItem, 
          itemsPerPage: item.key as number, 
          currentPage: 1, 
          loading: false,
          errorMessage: null
        }));
      }
    } catch (error) {
      // Handle unexpected errors
      const errorMessage = 'An unexpected error occurred during search';
      setState((prevState) => ({ 
        ...prevState, 
        errorMessage: errorMessage,
        loading: false,
        isResultsFetched: false,
        isDetailsListVisible: false
      }));
      // Display error message in parent component
      displayMessageBar(MessageBarType.error, errorMessage, false);
    }
  };

  const _onPageChange = async (selectedPage: number) => {
    setState((prevState) => ({
      ...prevState,
      loading: true,
      errorMessage: null,
    }));

    try {
      const data = await searchCriteriaForm(
          _handleSearch(),
          selectedPage,
          state.itemsPerPage,
          state.isExactOnly,
          state.isAkaEnabled,
          auth.user?.id_token
      );

      // Check if the response is an error
      if ('errors' in data) {
        // Handle validation errors
        const errorMessages = data.errors?.join(', ') || 'An error occurred during search';
        setState((prevState) => ({ 
          ...prevState, 
          errorMessage: errorMessages,
          loading: false,
          isResultsFetched: false,
          isDetailsListVisible: false
        }));
        // Display error message in parent component
        displayMessageBar(MessageBarType.error, errorMessages, false);
      } else {
        setState((prevState) => {
          return { 
            ...prevState, 
            searchResult: data as SearchResultItem, 
            currentPage: selectedPage, 
            loading: false,
            errorMessage: null
          };
        });
      }
    } catch (error) {
      // Handle unexpected errors
      const errorMessage = 'An unexpected error occurred during search';
      setState((prevState) => ({ 
        ...prevState, 
        errorMessage: errorMessage,
        loading: false,
        isResultsFetched: false,
        isDetailsListVisible: false
      }));
      // Display error message in parent component
      displayMessageBar(MessageBarType.error, errorMessage, false);
    }
  };

  function _onRenderSettingsIcon() {
    return <EiopaFnPIcons iconName={"actions"} iconColor={"#0078D4"}/>;
  }

  function _onRenderActionsMenuIcon() {
      return <></>;
  }

  function _onRenderCreateRequestIcon(isDisabled: boolean) {
    return <EiopaFnPIcons iconName={"createRequest"} iconColor={isDisabled ? "#A19F9D" : "#0078D4"}/>;
  }
  function _onRenderViewAssessmentsIcon(isDisabled: boolean) {
    return <EiopaFnPIcons iconName={"viewAssessments"} iconColor={"#0078D4"}/>;
  }

  function GetMenu(props: IContextualMenuProps): JSX.Element {
    const [loading, isLoading] = useState(true);
    useEffect(() => {
      (function () {
        fetchAssessmentStatus(props.id??"", auth.user?.id_token).then((resp) => {
          props.items.forEach(item => {
            if (item.key === "create") {
                item.disabled = resp.hasAssessmentsOnlyForNca;
                item.onRenderIcon = () => _onRenderCreateRequestIcon(resp.hasAssessmentsOnlyForNca);
            }
            if (item.key === "viewAssessments") {
                item.onRenderIcon = () => _onRenderViewAssessmentsIcon(false);
            }
          })
          isLoading(false)
        }).catch();
      })();
      return () => { };
    }, []);
    return (
        loading ? <Spinner size={0} style={{ padding: "10px" }} /> : <ContextualMenu {...props} />
    );
  }

  // Function to render columns
  const renderSearchItemColumn = (
      item: any,
      index: number | undefined,
      column: IColumn | undefined
  ): React.ReactNode => {

    if (item && column) {
      const fieldContent = item[column.fieldName as string] as string;

      if (column.fieldName === "RelevanceScore") {

        if (item.IsExactMatch) {
          return (
              <span>
              <Icon iconName="Flag" style={{ color: "green", fontSize: 20 }} />
            </span>
          );
        }
        return item.RelevanceScore;
      } else
      if (column.fieldName === "Actions") {
        const menuProps: IContextualMenuProps = {
          items: [
            {
              key: "viewAssessments",
              text: "View assessments",
              iconProps: { iconName: "BulletedList2" },
              onClick: () =>
                  handleItemClick(
                      strings.viewAssessmentsLabel,
                      item.personId,
                      item.EncryptedId,
                      item.PersonType === "Natural"
                          ? `${item.FirstName} ${item.LastName}` : `${item.LegalName}`
                  ),
            },
          ],
          directionalHintFixed: false,
          isBeakVisible: false,
          id: item.EncryptedId,
        };

        // Check if the user is ESAEndUser
        if (!isESAEndUser) {
          menuProps.items.push({
            key: "create",
            text: "Create request",
            iconProps: { iconName: "PageEdit" },
            onClick: () =>
                handleItemClick(
                    strings.selectCARecipientsLabel,
                    item.personId,
                    item.EncryptedId,
                    item.PersonType === "Natural"
                        ? `${item.FirstName} ${item.LastName}` : `${item.LegalName}`
                ),
          });
        }

        return (
            <IconButton
                menuProps={menuProps}
                menuAs={GetMenu}
                onRenderIcon={_onRenderSettingsIcon}
                onRenderMenuIcon={_onRenderActionsMenuIcon}
                title="Actions"
                ariaLabel="Actions"
            />
        );
      }

      else {
        return <TooltipHost overflowMode={TooltipOverflowMode.Parent} content={fieldContent}>{fieldContent}</TooltipHost>;
      }
    }
  };

  return (
      <Stack className={styles.SearchForPoiStack}>
        <StackItem className={styles.SearchPoiTopLabel}>
          {strings.searchPoi}
        </StackItem>

        <SearchForm
            typeOptions={typeOptions}
            _handleDropDownChange={_handleDropDownChange}
            selectedKey={state.selectedKey}
            _handleSearchItem={_handleSearchItem}
            searchCriteria={state.searchCriteria}
            _handleDateChange={_handleDateChange}
            _isExactMatch={_isExactMatch}
            isExactOnly={state.isExactOnly}
            _isAkaEnabled={_isAkaEnabled}
            isAkaEnabled={state.isAkaEnabled}
            _onSearchClick={_onSearchClick}
            _onReset={_onReset}
            isDisabled={isDisabled}
            isGroupNaturalRequired={isGroupNaturalRequired}
            isGroupLegalRequired={isGroupLegalRequired}
            // errorMessage={getErrorMessageDateNew}
            dateOfBirth={dateOfBirth}
            setDateString={setDateString}
            dateString={dateString}
            hasErrorMessage={setHasErrorMessage}
            resetFlag={resetFlag}
        />
        <StackItem>
          <Stack>
            <StackItem>
              {state.loading ? (
                  <Spinner label={strings.spinnerLabel} />
              ) : state.isResultsFetched && state.isDetailsListVisible ? (
                  <>
                    {(_items().length === 0 && state.isExactOnly) ||
                    _items().length === 0 ? (
                        <Stack styles={stackStylesNoResults}>
                          {strings.noResultsText}
                        </Stack>
                    ) : (
                        <Stack>
                          <StackItem styles={detailsListContainer}>
                            <PersonOfInterestDetailsList
                                userInfo={userInfo}
                                items={_items()}
                                renderItemColumn={renderSearchItemColumn}
                                type={state.selectedKey}
                                columns={_columns}
                                handleItemClick={handleItemClick}
                            />
                          </StackItem>
                          {isModalOpen && (
                              <Assessment
                                  hideModal={hideModal}
                                  isModalOpen={isModalOpen}
                                  readOnly={false}
                                  modalAction={state.modalAction}
                                  personId={state.clickedPersonId}
                                  EncryptedId={state.EncryptedId}
                                  name={state.name}
                              />
                          )}
                          <StackItem styles={stackStylesPagination}>
                            <br />
                            <Pagination
                                currentPage={state.currentPage}
                                onPageChange={_onPageChange}
                                itemsPerPage={state.itemsPerPage}
                                onChangeItemsPerPage={_onChangeItemsPerPage}
                                totalItems={state.searchResult.totalItems}
                            />
                          </StackItem>
                        </Stack>
                    )}
                  </>
              ) : null}
            </StackItem>
          </Stack>
        </StackItem>
      </Stack>
  );
}

export default SearchForPoi;
